/* eslint-disable react-hooks/immutability */
import type { OnboardingApplicationHydrated } from '../../lib/flotiq-client/types/api-response';
import type { UserLocalStorage } from '../../types/localStorage';

import { useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import useLocalStorageState from 'use-local-storage-state';

import { ResponseError } from '../../lib/flotiq-client/response-errors';
import { getTranslatedField } from '../../lib/helpers';

import useSelectedSpace from '../../hooks/useSelectedSpace';
import useToken from '../../hooks/useToken';

import { useModals } from '../../contexts/ModalContext';

import PopulateSpaceErrorContent from './content/PopulateSpaceErrorContent';
import PopulateSpaceModalContent from './content/PopulateSpaceIntruductionContent';
import PopulateSpaceModalLoading from './content/PopulateSpaceModalLoading';
import PopulateSpaceReadyModal from './content/PopulateSpaceReadyModal';
import getObjectToNavigate from './populateSpaceSteps/contentObjects';
import generateForms from './populateSpaceSteps/generateForms';
import getApiKeys from './populateSpaceSteps/getGlobalApiKeys';
import { PopulateSpaceError } from './populateSpaceSteps/PopulateSpaceError';
import syncSpace from './populateSpaceSteps/syncSpace';
import { updatePlugins } from './populateSpaceSteps/updatePlugins';

type ApiKeys = { rwApiKey: string; roApiKey?: string };

const populateSpaceModalId = 'populate-space-modal-id';

export const usePopulateSpaceModal = (testId?: string) => {
  const jwt = useToken();
  const modal = useModals();
  const [user] = useLocalStorageState<UserLocalStorage>('cms.user');
  const { t, i18n } = useTranslation();
  const { buildUrlWithSpace } = useSelectedSpace();

  const closeLoadingModal = useCallback(
    () => modal.close('populate-space-modal-loading'),
    [modal],
  );

  const closePopulateSpaceModals = useCallback(() => {
    closeLoadingModal();
    modal.close(populateSpaceModalId);
  }, [closeLoadingModal, modal]);

  const openLoadingModal = useCallback(
    async (dialogAdditionalClasses: string = 'opacity-100') =>
      modal({
        id: 'populate-space-modal-loading',
        dialogAdditionalClasses: dialogAdditionalClasses,
        content: <PopulateSpaceModalLoading testId={testId} />,
        size: 'xl',
        contentAdditionalClasses: 'pb-4 lg:pb-12',
        hideClose: true,
      }),
    [modal, testId],
  );

  const getGlobalApiKeys = useCallback(async () => {
    if (!jwt) {
      return;
    }

    openLoadingModal('opacity-100');

    try {
      const apiKeys = await getApiKeys(jwt, t);
      return apiKeys;
    } catch (error) {
      let message: string;
      openLoadingModal('opacity-0');

      if (!(error instanceof ResponseError)) {
        toast.error(t('Form.CommunicationErrorMessage'));
        message = t('Form.CommunicationErrorMessage');
      } else {
        message = error.message;
      }

      const tryAgain = await modal({
        id: 'populate-space-modal-error-id',
        size: 'xl',
        content: (
          <PopulateSpaceErrorContent testId={testId} errorMessage={message} />
        ),
        contentAdditionalClasses: 'pb-7 lg:pb-11',
        hideClose: true,
      });

      if (tryAgain) {
        return getGlobalApiKeys();
      }
    }
  }, [jwt, modal, openLoadingModal, t, testId]);

  const runPopulateSpace = useCallback(
    async (
      apiKeys: ApiKeys,
      sourceApiKey: string,
      step: number,
      userId: string,
    ) => {
      const { rwApiKey, roApiKey } = apiKeys;
      if (step === 0) await syncSpace(rwApiKey, sourceApiKey);
      if (step <= 1) await updatePlugins(rwApiKey, roApiKey, jwt);
      await generateForms(rwApiKey, sourceApiKey, userId, t, jwt);
    },
    [jwt, t],
  );

  const processPopulateSpace = useCallback(
    async (
      apiKeys: ApiKeys,
      application: OnboardingApplicationHydrated,
      userId: string,
      step: number = 0,
    ) => {
      const sourceApiKey = getTranslatedField(
        application,
        'space_ro_api_key',
        i18n.language,
      );
      const mainContentType = getTranslatedField(
        application,
        'main_content_type',
        i18n.language,
      );

      try {
        await runPopulateSpace(apiKeys, sourceApiKey, step, userId);

        const objectToNavigate = await getObjectToNavigate(
          jwt,
          mainContentType,
        );

        closePopulateSpaceModals();

        const goEdit = await modal({
          id: 'populate-space-ready-modal',
          content: (
            <PopulateSpaceReadyModal
              videoUrl={getTranslatedField(
                application,
                'demo_video_url',
                i18n.language,
              )}
              testId={testId}
            />
          ),
          size: 'xl',
          contentAdditionalClasses: 'pb-10',
        });

        if (goEdit && objectToNavigate?.id) {
          window.location.replace(
            buildUrlWithSpace(
              `content-type-objects/edit/${mainContentType}/${objectToNavigate.id}?livePreviewOpened=true`,
            ),
          );
        } else {
          window.location.reload();
        }
      } catch (error) {
        if (error instanceof Error) {
          openLoadingModal('opacity-0');

          const tryAgain = await modal({
            id: 'populate-space-modal-error-id',
            size: 'xl',
            content: (
              <PopulateSpaceErrorContent
                testId={testId}
                errorMessage={error.message}
              />
            ),
            contentAdditionalClasses: 'pb-4 lg:pb-12',
            hideClose: true,
          });

          if (!tryAgain) {
            closePopulateSpaceModals();
            window.location.reload();
            return;
          }

          openLoadingModal();

          return processPopulateSpace(
            apiKeys,
            application,
            userId,
            error instanceof PopulateSpaceError ? error.type : 0,
          );
        }
      }
    },
    [
      buildUrlWithSpace,
      closePopulateSpaceModals,
      i18n.language,
      jwt,
      modal,
      openLoadingModal,
      runPopulateSpace,
      testId,
    ],
  );

  const onApplicationSelect = useCallback(
    async (application: OnboardingApplicationHydrated) => {
      const apiKeys = await getGlobalApiKeys();
      if (!apiKeys) {
        closePopulateSpaceModals();
        return;
      }
      await processPopulateSpace(apiKeys, application, user!.data.id, 0);
    },
    [closePopulateSpaceModals, getGlobalApiKeys, processPopulateSpace, user],
  );

  const openPopulateSpaceModal = useCallback(async () => {
    if (!user || !jwt) {
      return;
    }

    await modal({
      id: populateSpaceModalId,
      content: (
        <PopulateSpaceModalContent
          userName={user.data.firstName}
          onApplicationSelect={onApplicationSelect}
          testId={testId}
        />
      ),
      size: 'inset',
      dialogAdditionalClasses: 'bg-slate-50',
      contentAdditionalClasses: 'h-full p-4 lg:p-12',
      hideClose: true,
    });
  }, [user, jwt, modal, testId, onApplicationSelect]);

  return { openPopulateSpaceModal };
};
